import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import { assetService } from "../../Core/Api/services";
import {
  AdminAssetSingleActionsDto,
  AdminAssetSingleHierarchyDto,
  AdminAssetSingleMetadataDto,
  DbAssetObjectInfos,
  TitleContentData,
} from "../../Core/Api/Types/db-asset-object-infos";
import { DbFileInfos } from "../../Core/Api/Types/db-file-infos";
import { DbJobInfos } from "../../Core/Api/Types/db-job-infos";
import { DbWorkflowInstanceInfos } from "../../Core/Api/Types/db-workflow-instance-infos";
import { GetGraphResponse } from "../../Core/Api/Types/get-graph-response";
import { HierarchyTree } from "../../Core/Api/Types/hierarchy-tree";
import GlobalConst from "../../Core/Utils/Constants/globals";
import { RootState } from "../redux-store";
import { GetChildRecursivelyObjectById } from "../../Core/Utils/asset-object-utils";
import { DbDeliveryMetadataExportInfos } from "../../Core/Api/Types/db-delivery-metadata-export-infos";

export interface AssetRightActive {
  show: boolean;
  active: boolean;
}

type AddChildrenType = {
  parentObjectId: number;
  children: DbAssetObjectInfos[];
};

type RemoveChildrenType = {
  childrenIds: number[];
};
type AddChildrenByIdType = {
  parentObjectId: number;
  childrenIds: number[];
};

type JobOrWorklow = DbJobInfos | DbWorkflowInstanceInfos;

export function isJob(jobOrWorkflow: JobOrWorklow): jobOrWorkflow is DbJobInfos {
  return (jobOrWorkflow as any).type !== null && (jobOrWorkflow as any).type !== undefined;
}
export function isWorkflow(jobOrWorkflow: JobOrWorklow): jobOrWorkflow is DbWorkflowInstanceInfos {
  return (jobOrWorkflow as any).definitionType !== null && (jobOrWorkflow as any).definitionType !== undefined;
}

export interface AssetSliceData {
  // Object native asset
  obj: GetGraphResponse | undefined;
  panelObj: DbAssetObjectInfos | undefined;
  dto: {
    objMeta: AdminAssetSingleMetadataDto | undefined;
    panelObj: AdminAssetSingleMetadataDto | undefined;
    objHierarchy: AdminAssetSingleHierarchyDto | undefined;
    objActions: AdminAssetSingleActionsDto | undefined;
    titleContent: TitleContentData | undefined;
  };
  // Asset single Media files tab data
  files: DbFileInfos[] | undefined;
  // Asset single Actions tab data (job + workflows)
  jobsAndWorklows: JobOrWorklow[] | undefined;
  // title preview
  episode: number | null;
  isLoading: boolean;
  assetRightActive: AssetRightActive;
  resultObject: DbAssetObjectInfos[];
  areLazyLoad: string[];
  cachedResults: { [key: string]: DbAssetObjectInfos[] };
  update: number;
  exports: DbDeliveryMetadataExportInfos[] | null;
}

const initialState: AssetSliceData = {
  obj: undefined,
  panelObj: undefined,
  dto: {
    objActions: undefined,
    objHierarchy: undefined,
    objMeta: undefined,
    panelObj: undefined,
    titleContent: undefined,
  },
  jobsAndWorklows: undefined,
  files: undefined,
  episode: null,
  isLoading: true,
  //   treeObject: null,
  //   hilightedObjectId: undefined,
  assetRightActive: { show: false, active: false },
  resultObject: [],
  areLazyLoad: [],
  //   cachedAssetToAddInTree: [],
  cachedResults: {},
  update: 0,
  exports: null,
};

export const RefreshCurrentObject: any = createAsyncThunk(
  "assetSlice/RefreshCurrentObject",
  async (_, thunkAPI): Promise<GetGraphResponse | undefined> => {
    const assetSlice = (thunkAPI.getState() as RootState).assetSlice;

    const assetId = assetSlice.obj?.graph?.id;
    if (!assetId) return;

    thunkAPI.dispatch(SetLoading(true));

    const newGraphReponse = await assetService.GetObjectById(assetId);

    return newGraphReponse;
  }
);

const computeAllDto = (obj, panelObj) => {
  return {
    objMeta: !obj || !obj.graph ? undefined : assetService.GetAdminAssetSingleMetadataDtoFromGraphResponse(obj),
    panelObj:
      !panelObj || !panelObj.graph ? undefined : assetService.GetAdminAssetSingleMetadataDtoFromGraphResponse(panelObj),
    objHierarchy:
      !obj || !obj.hierarchy ? undefined : assetService.GetAdminAssetSingleHierarchyDtoFromGraphResponse(obj),
    objActions: !obj || !obj.graph ? undefined : assetService.GetAdminAssetSingleActionsDtoFromAssetObject(obj.graph),
    titleContent: !obj || !obj.graph ? undefined : assetService.GetTitleContentDataFromAssetObject(obj),
  };
};

const assetSlice = createSlice({
  name: "assetSlice",
  initialState,

  reducers: {
    InitState: (state) => {
      return initialState;
    },
    AddToLazyLoad: (state, action: PayloadAction<string[]>) => {
      state.areLazyLoad = state.areLazyLoad.concat(action.payload);
    },
    RemoveFromResultObjectsById: (state, action: PayloadAction<number[]>) => {
      state.resultObject = state.resultObject.filter((x) => !action.payload.find((y) => y === x.id));
    },
    SetResultObjects: (state, action: PayloadAction<DbAssetObjectInfos[]>) => {
      return {
        ...state,
        resultObject: action.payload,
      };
    },
    SetFetchedObject: (state, action: PayloadAction<GetGraphResponse>) => {
      if (action.payload.hierarchy === null && state.obj) action.payload.hierarchy = state.obj?.hierarchy;
      if (action.payload.layer === null && state.obj) action.payload.layer = state.obj?.layer;
      if (action.payload.views === null && state.obj) action.payload.views = state.obj?.views;
      const keepPerms =
        action.payload.graph &&
        state.obj?.graph &&
        action.payload.graph.id === state.obj.graph.id &&
        action.payload.graph.permsInherit === null &&
        state.obj.graph.permsInherit !== null;
      return {
        ...state,
        obj: keepPerms
          ? { ...action.payload, graph: { ...action.payload.graph!, permsInherit: state.obj!.graph!.permsInherit! } }
          : action.payload,
        dto: computeAllDto(action.payload, state.panelObj),
        jobsAndWorklows: undefined,
        files: undefined,
        areLazyLoad: [],
      };
    },
    SetObjectMetadataViewIds: (state, action: PayloadAction<number>) => {
      if (!state.obj?.graph) return;
      if (state.obj?.graph?.viewIds?.includes(action.payload))
        state.obj.graph.viewIds = [...state.obj?.graph?.viewIds.filter((x) => x != action.payload)];
      else state.obj.graph.viewIds = [...(state.obj?.graph?.viewIds ?? []), action.payload];
    },
    ResetFetchedObject: (state) => {
      return {
        ...state,
        panelObj: undefined,
        dto: {
          ...state.dto,
          panelObj: undefined,
        },
      };
    },
    //  Asset single Media files tabs reducers
    SetFetchedMediaFiles: (state, action: PayloadAction<DbFileInfos[]>) => {
      return {
        ...state,
        files: action.payload,
      };
    },
    UpdateMediaFile: (state, action: PayloadAction<DbFileInfos>) => {
      if (state.files) {
        let fileIndex = state.files.findIndex((x) => x.id === action.payload.id);
        if (fileIndex !== -1) {
          state.files[fileIndex] = action.payload;
        }
      }
    },
    DeleteMediaFile: (state, action: PayloadAction<number>) => {
      if (state.files) {
        const indexToDelete = state.files.findIndex((x) => x.id === action.payload);
        if (indexToDelete !== -1) state.files.splice(indexToDelete, 1);
      }
    },
    // Asset single Actions tab reducer
    SetFetchedJobsAndWorkflows: (
      state,
      action: PayloadAction<{ jobs: DbJobInfos[]; workflows: DbWorkflowInstanceInfos[] }>
    ) => {
      const { jobs, workflows } = action.payload;
      return {
        ...state,
        jobsAndWorklows: [...jobs, ...workflows],
      };
    },
    AddOrUpdateJobOrWorkflow: (state, action: PayloadAction<JobOrWorklow>) => {
      const payload = { ...action.payload };
      let toUpdate = state.jobsAndWorklows?.find((x) => x.id === action.payload.id);
      if (toUpdate) toUpdate = payload;
      else state.jobsAndWorklows = state.jobsAndWorklows ? [...state.jobsAndWorklows, payload] : [payload];
    },
    DeleteJobOrWorklow: (state, action: PayloadAction<number>) => {
      state.jobsAndWorklows = state.jobsAndWorklows?.filter((x) => x.id !== action.payload);
    },
    // Is Loading (title)
    SetLoading: (state, action) => {
      return {
        ...state,
        isLoading: action.payload,
      };
    },

    SetActiveRight: (state, action: PayloadAction<AssetRightActive>) => {
      return {
        ...state,
        assetRightActive: action.payload,
      };
    },

    AddChildrenCurrentAsset: (state, action: PayloadAction<AddChildrenType>) => {
      //TODO : need to improv.
      /// Main graph

      /// Hierachy graph (from top bar)
      if (state?.obj?.graph === null || state.obj?.graph === undefined) return;

      if (state.obj.graph.id === action.payload.parentObjectId) {
        {
          state.obj.graph.childObjects = (state.obj.graph.childObjects ?? []).concat(action.payload.children);
          state.obj.graph = { ...state.obj.graph };
        }
      } else {
        let parent: DbAssetObjectInfos | null = GetChildRecursivelyObjectById(
          state.obj.graph,
          action.payload.parentObjectId
        );
        if (parent === null) return;

        const toAdd: DbAssetObjectInfos[] = action.payload.children.filter(
          (x) => parent?.childObjects?.find((y) => y.id === x.id) === undefined
        );

        parent.childObjects = (parent.childObjects ?? []).concat(toAdd);
      }
    },
    AddCreatedObjects: (
      state,
      action: PayloadAction<{ createdObjects: DbAssetObjectInfos[]; temporaryUploadingName?: string }>
    ) => {
      if (!state.obj?.graph?.id) return;

      for (let createdObject of action.payload.createdObjects) {
        if (createdObject.parentObjectId === state.obj!.graph.id)
          state.resultObject = state.resultObject
            .filter(
              (x) => !(x.id === -500 && x.name === action.payload.temporaryUploadingName) && x.id !== createdObject.id
            )
            .concat(createdObject);
      }
    },
    AddUploadingObject: (state, action: PayloadAction<DbAssetObjectInfos>) => {
      state.resultObject = state.resultObject
        .filter((x) => x.id !== -500 || x.name !== action.payload.name)
        .concat(action.payload);
    },
    RemoveUploadingObject: (state, action) => {
      state.resultObject = state.resultObject.filter((x) => x.name !== action.payload || x.id !== -500);
    },
    RemoveAllUploadObjects: (state) => {
      state.resultObject = state.resultObject.filter((x) => x.id !== -500);
    },
    CacheResults: (state, action) => {
      state.cachedResults = { ...state.cachedResults, [action.payload.page]: action.payload.results };
    },
    IncrementUpdate: (state) => {
      state.update++;
    },
    SetExports: (state, action: PayloadAction<DbDeliveryMetadataExportInfos[]>) => {
      return {
        ...state,
        exports: action.payload,
      };
    },
  },
  // https://stackoverflow.com/questions/72420654/redux-toolkit-extrareducers-listed-after-reducers-causes-issues-ts2322-error
  extraReducers: (builder: ActionReducerMapBuilder<AssetSliceData>) => {
    builder.addCase(RefreshCurrentObject.fulfilled, (state, action) => {
      return {
        ...state,
        isLoading: false,
        obj: action.payload,
      };
    });
  },
});

export const {
  InitState,
  AddToLazyLoad,
  RemoveFromResultObjectsById,
  SetResultObjects,
  SetFetchedObject,
  ResetFetchedObject,
  SetFetchedMediaFiles,
  SetFetchedJobsAndWorkflows,
  UpdateMediaFile,
  DeleteMediaFile,
  SetLoading,
  SetActiveRight,
  AddChildrenCurrentAsset,
  SetObjectMetadataViewIds,
  AddCreatedObjects,
  AddUploadingObject,
  RemoveUploadingObject,
  RemoveAllUploadObjects,
  CacheResults,
  IncrementUpdate,
  SetExports,
  AddOrUpdateJobOrWorkflow,
  DeleteJobOrWorklow,
} = assetSlice.actions;

export const GetObjectMetadataMetadataDto = (state: RootState): AdminAssetSingleMetadataDto | undefined =>
  state.assetSlice.dto.objMeta;

export const GetObjectHierarchyDto = (state: RootState): AdminAssetSingleHierarchyDto | undefined =>
  state.assetSlice.dto.objHierarchy;

export const GetObjectMediaFiles = (state: RootState): AssetSliceData["files"] => state.assetSlice.files;

export const GetObjectActionsDto = (state: RootState): AdminAssetSingleActionsDto | undefined =>
  state.assetSlice.dto.objActions;
export const GetObjectJobs = (state: RootState): AssetSliceData["jobsAndWorklows"] => state.assetSlice.jobsAndWorklows;

export const GetObject = (state: RootState): DbAssetObjectInfos | undefined => state.assetSlice.obj?.graph || undefined;
export const IsObjectLoading = (state): boolean => state.assetSlice.isLoading;

export const GetTitleContentData = (state: RootState): TitleContentData | undefined =>
  state.assetSlice.dto.titleContent;

export const GetTitleActiveRightData = (state: RootState): AssetRightActive => {
  return state.assetSlice.assetRightActive;
};
const GetFiles = (state: RootState): DbFileInfos[] | undefined => state.assetSlice.files;

export const GetObjectUploadedMediaFilesFormatsIds = createSelector(
  [GetFiles],
  (files: DbFileInfos[] | undefined): number[] =>
    files?.filter((f) => f.formatInfos).map((file) => file.formatInfos!.id) || []
);

export const GetResultObjects = (state: RootState): DbAssetObjectInfos[] => {
  return state.assetSlice.resultObject;
};

export const GetAreLazyLoad = (state: RootState): string[] => {
  return state.assetSlice.areLazyLoad;
};

export const GetCachedResults = (page: string) => (state: RootState) => {
  return state.assetSlice.cachedResults[page];
};

export const GetUpdateCounter = (state: RootState): number => {
  return state.assetSlice.update;
};

export const GetExports = (state: RootState): DbDeliveryMetadataExportInfos[] | null => {
  return state.assetSlice.exports;
};

export default assetSlice.reducer;
